import React, {createContext, useContext, useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {jwtDecode} from "jwt-decode";
import {mergeStateObject} from "../utils/merger";
import {fetchWrapper} from "../helpers";
import {Button, message, notification, Space} from "antd";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

export const OrganizationContextProvider = createContext(null);

export const useOrganization = () => useContext(OrganizationContextProvider);

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const SHOWN_KEY = "shownModerationNotification";
const FP_KEY = "authFingerprint";
const LAST_ACTIVE_KEY = "lastActiveAt";
const INACTIVITY_MS = 30 * 60 * 1000;

const getAuthFingerprint = (token, fallbackUserId) => {
    try {
        if (!token) return null;
        const payload = jwtDecode(token);
        const authTime = payload?.auth_time ?? null;
        const sub = payload?.sub ?? fallbackUserId ?? "unknown";
        const orgId = payload?.organizationId ?? "no-org";
        return authTime ? `auth:${sub}:${orgId}:${authTime}` : `sub:${sub}:${orgId}`;
    } catch {
        return null;
    }
};

const OrganizationContext = ({children}) => {
	const [notificationApi, notificationContextHolder] =
			notification.useNotification();
	const navigate = useNavigate();
	const {t} = useTranslation();
	const user = useSelector((obj) => obj.auth.user);
	const [organization, setOrganization] = useState(null);

    useEffect(() => {
        const currentFp = getAuthFingerprint(user?.accessToken, user?.id);

        if (!currentFp) return;

        const storedFp = sessionStorage.getItem(FP_KEY);

        if (storedFp !== currentFp) {
            sessionStorage.removeItem(SHOWN_KEY);
            sessionStorage.setItem(FP_KEY, currentFp);
        }
    }, [user?.accessToken, user?.id]);

	const handleSetOrganization = (organization) => {
		mergeStateObject(setOrganization, organization);
	};

	const getOrganizationDetails = async (id) => {
		if (!id) return {};
		const response = await fetchWrapper.get(`${baseUrl}/organizations/${id}`);
		return response;
	};

	const setOrganizationFile = async (file) => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await fetchWrapper.post(`${baseUrl}/organizations/${organization?.id}/files`, formData);
		if (response.fileUrl){
			handleSetOrganization({fileUrl: response.fileUrl});
			message.success(t("functions.successfullyUploaded"));
		} else {
			message.error(t("functions.wrong"));
		}
	};

	const setOrganizationLogo = async (file) => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await fetchWrapper.post(`${baseUrl}/organizations/${organization?.id}/logo`, formData);
		if (response?.logoUrl){
			handleSetOrganization({logoUrl: response.logoUrl});
			message.success(t("functions.successfullyUploaded"));
		} else {
			message.error(t("functions.wrong"));
		}
	};

	const openNotificationUnreadMessages = (count) => {
		const key = `open${Date.now()}`;
		const btn = (
				<Space>
					<Button
							type="primary"
							size="small"
							onClick={() => navigate("/moderation")}
					>
						{t("moderation.goToModeration")}
					</Button>
				</Space>
		);
		notificationApi.open({
			message: t("moderation.header"),
			description: t("moderation.unreadModerationCountMessage", {count: count}),
			btn,
			key,
		});
	};

	const checkModerationMessages = async () => {
		const response = await fetchWrapper.get(`${baseUrl}/post/moderation?page=1&limit=11&approvedExtended=user_approved`);
		const unreadMessages = await response?.posts?.length ?? 0;

		// Не показываем уведомление на странице media insights и на странице модерации
		if (unreadMessages && !window.location.pathname.includes('moderation') && !window.location.pathname.includes('/analytics/media/')) {
			openNotificationUnreadMessages(unreadMessages > 9 ? '9+' : unreadMessages);
		}
	};

	useEffect(() => {
		let orgFromToken = null;
		(async () => {
			if (user){
				if (jwtDecode(user?.accessToken)?.organizationId){
					orgFromToken = {
						id: jwtDecode(user?.accessToken)?.organizationId,
						curator: jwtDecode(user?.accessToken)?.curator
					}
				} else {
					orgFromToken = {
						id: user?.organizationId,
						curator: user?.curator
					}
				}
				const orgDetails = await getOrganizationDetails(orgFromToken?.id);
				handleSetOrganization({
					...orgFromToken,
					...orgDetails
				});
			}
		})();
	}, [user]);

    useEffect(() => {
        if (organization?.moderatorExist && user?.moderatorOrg) {
            const hasShown = sessionStorage.getItem(SHOWN_KEY);

            if (!hasShown) {
                checkModerationMessages().finally(() => {
                    sessionStorage.setItem(SHOWN_KEY, "true");
                });
            }
        }
    }, [user, organization]);

    useEffect(() => {

        const shouldCheck = () =>
            organization?.moderatorExist && user?.moderatorOrg;

        const checkIfInactiveLong = () => {
            const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || "0", 10);
            const inactiveLong = Date.now() - last >= INACTIVITY_MS;
            if (inactiveLong && shouldCheck()) {

                checkModerationMessages();
            }
        };

        const markActivity = () => {

            checkIfInactiveLong();
            localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        };
        localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));


        const activityEvents = ["mousemove", "keydown", "click", "touchstart", "wheel"];
        activityEvents.forEach((e) => window.addEventListener(e, markActivity, {passive: true}));


        const onFocusOrVisible = () => {
            checkIfInactiveLong();
            localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        };
        window.addEventListener("focus", onFocusOrVisible);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") onFocusOrVisible();
        });

        return () => {
            activityEvents.forEach((e) => window.removeEventListener(e, markActivity));
            window.removeEventListener("focus", onFocusOrVisible);
            document.removeEventListener("visibilitychange", onFocusOrVisible);
        };
    }, [user?.moderatorOrg, organization?.moderatorExist]);

	return (
			<OrganizationContextProvider.Provider value={{organization, setOrganizationFile, setOrganizationLogo}}>
				{notificationContextHolder}
				{children}
			</OrganizationContextProvider.Provider>
	);
};

export default OrganizationContext;